/**
 * Assegnazione CVE a un Ticket manuale tramite typeahead sulle CVE note.
 *
 * Pensato per cataloghi molto grandi (mirror NVD in Dependency-Track): il portale non scarica
 * mai l'elenco. La ricerca è lato server (GET /cve/search), parte con ≥3 caratteri dopo 300 ms
 * di quiete, annulla le richieste superate e mostra al più 10 risultati, con un invito ad
 * affinare la ricerca se ce ne sono altri. Navigabile da tastiera (↑/↓/Invio/Esc).
 */

import { useId, useState } from 'react';
import { ApiError } from '../../api/client.js';
import { useAssignTicketCve, useKnownCveSearch } from './hooks.js';
import { CVE_SEARCH_MIN_QUERY, type KnownCve } from './types.js';
import styles from './Triage.module.css';

/** Risultati mostrati per pagina di ricerca. */
const LIMITE_RISULTATI = 10;

function classeSeverita(severity: string | undefined): string {
  switch ((severity ?? '').toUpperCase()) {
    case 'CRITICAL':
      return styles.badgeCritical ?? '';
    case 'HIGH':
      return styles.badgeHigh ?? '';
    case 'MEDIUM':
      return styles.badgeMedium ?? '';
    case 'LOW':
      return styles.badgeLow ?? '';
    default:
      return styles.badgeInfo ?? '';
  }
}

function messaggioErrore(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Errore sconosciuto';
}

export function CvePicker({
  ticketId,
  giaAssegnate,
  inline = false,
}: {
  ticketId: number;
  /** CVE già presenti nel ticket (mostrate come non selezionabili). */
  giaAssegnate: string[];
  /** Senza scheda e titolo visibile: per l'uso dentro uno stato vuoto che già spiega l'azione. */
  inline?: boolean;
}) {
  const listId = useId();
  const [query, setQuery] = useState('');
  const [aperto, setAperto] = useState(false);
  const [attivo, setAttivo] = useState(0);
  const ricerca = useKnownCveSearch(query, LIMITE_RISULTATI);
  const assegna = useAssignTicketCve(ticketId);

  const assegnate = new Set(giaAssegnate.map((c) => c.toUpperCase()));
  const queryCorta = query.trim().length < CVE_SEARCH_MIN_QUERY;
  const items = queryCorta ? [] : (ricerca.data?.items ?? []);
  const inAttesa = !queryCorta && (ricerca.isFetching || ricerca.debouncedQuery !== query.trim());

  function scegli(cve: KnownCve) {
    if (assegnate.has(cve.cveId) || assegna.isPending) return;
    assegna.mutate(cve, {
      onSuccess: () => {
        setQuery('');
        setAperto(false);
        setAttivo(0);
      },
    });
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAperto(true);
      setAttivo((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setAttivo((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const scelta = items[attivo];
      if (aperto && scelta) scegli(scelta);
    } else if (e.key === 'Escape') {
      setAperto(false);
    }
  }

  return (
    <div className={inline ? undefined : styles.formCard} data-testid={`cve-picker-${ticketId}`}>
      <label className={inline ? styles.srOnly : styles.formTitle} htmlFor={`${listId}-input`}>
        Assegna CVE
      </label>
      <div className={styles.comboWrap}>
        <input
          id={`${listId}-input`}
          type="search"
          role="combobox"
          aria-expanded={aperto && items.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={aperto && items[attivo] ? `${listId}-${attivo}` : undefined}
          className={styles.formSelect}
          style={{ width: '100%' }}
          placeholder="Cerca tra le CVE note (es. CVE-2024-3094)…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setAperto(true);
            setAttivo(0);
          }}
          onFocus={() => setAperto(true)}
          onBlur={() => setTimeout(() => setAperto(false), 150)}
          onKeyDown={onKeyDown}
          disabled={assegna.isPending}
          data-testid={`input-cerca-cve-${ticketId}`}
        />

        {aperto && items.length > 0 && (
          <ul id={listId} role="listbox" className={styles.comboList}>
            {items.map((c, i) => {
              const gia = assegnate.has(c.cveId);
              return (
                <li
                  key={c.cveId}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={i === attivo}
                  aria-disabled={gia}
                  className={`${styles.comboOption} ${i === attivo ? styles.comboOptionActive : ''} ${gia ? styles.comboOptionDisabled : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    scegli(c);
                  }}
                  data-testid={`opzione-cve-${c.cveId}`}
                >
                  <span style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                    <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{c.cveId}</span>
                    {c.severity && (
                      <span className={`${styles.badge} ${classeSeverita(c.severity)}`}>
                        {c.severity}
                      </span>
                    )}
                    {c.knownExploited && (
                      <span className={`${styles.badge} ${styles.badgeStateExploitable ?? ''}`}>
                        KEV
                      </span>
                    )}
                    {c.cvssScore !== undefined && (
                      <span className={styles.comboMeta}>CVSS {c.cvssScore.toFixed(1)}</span>
                    )}
                    <span className={styles.comboMeta}>{c.source}</span>
                    {gia && <span className={styles.comboMeta}>già assegnata</span>}
                  </span>
                  {c.description && <span className={styles.comboDesc}>{c.description}</span>}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <p
        className={styles.pickerHint}
        aria-live="polite"
        data-testid={`stato-cerca-cve-${ticketId}`}
      >
        {query.trim() === ''
          ? `Digita almeno ${CVE_SEARCH_MIN_QUERY} caratteri: la ricerca avviene sul database vulnerabilità (NVD/GHSA/OSV).`
          : queryCorta
            ? `Ancora ${CVE_SEARCH_MIN_QUERY - query.trim().length} caratteri…`
            : inAttesa
              ? 'Ricerca…'
              : ricerca.isError
                ? messaggioErrore(ricerca.error)
                : items.length === 0
                  ? 'Nessuna CVE trovata.'
                  : ricerca.data?.hasMore
                    ? `Primi ${items.length} risultati: affina la ricerca per vederne altri.`
                    : `${items.length} risultati.`}
      </p>
      {assegna.isError && (
        <span className={styles.inlineMutError} data-testid={`errore-assegna-cve-${ticketId}`}>
          {messaggioErrore(assegna.error)}
        </span>
      )}
    </div>
  );
}
